/**
 * Page Splitter Utility
 *
 * Splits TipTap JSON content into multiple pages based on pageBreak nodes.
 * Handles edge cases like empty pages, consecutive breaks, and page breaks at start/end.
 */

/**
 * Splits TipTap document JSON into separate pages based on pageBreak nodes
 *
 * @param {Object} tiptapJSON - TipTap document JSON (type: 'doc', content: [...])
 * @returns {Array<Object>} Array of page objects, each with TipTap JSON structure
 *
 * @example
 * const content = {
 *   type: 'doc',
 *   content: [
 *     { type: 'paragraph', content: [...] },
 *     { type: 'pageBreak' },
 *     { type: 'paragraph', content: [...] },
 *   ]
 * }
 *
 * const pages = splitContentByPageBreaks(content)
 * // Returns: [
 * //   { type: 'doc', content: [{ type: 'paragraph', ... }] },
 * //   { type: 'doc', content: [{ type: 'paragraph', ... }] }
 * // ]
 */
export function splitContentByPageBreaks(tiptapJSON) {
  // Handle null/undefined or empty content
  if (!tiptapJSON || !tiptapJSON.content || tiptapJSON.content.length === 0) {
    return [{ type: 'doc', content: [] }]
  }

  const pages = []
  let currentPage = []

  for (const node of tiptapJSON.content) {
    if (node.type === 'pageBreak') {
      // Found a page break - finalize current page
      if (currentPage.length > 0) {
        // Only add non-empty pages
        pages.push({ type: 'doc', content: currentPage })
        currentPage = []
      }
      // Note: We skip the pageBreak node itself - it's just a delimiter
      // Consecutive page breaks are automatically collapsed (empty currentPage check)
    } else {
      // Regular content node - add to current page
      currentPage.push(node)
    }
  }

  // Add final page if it has content
  if (currentPage.length > 0) {
    pages.push({ type: 'doc', content: currentPage })
  }

  // Edge case: Document with only page breaks or empty
  // Return at least one empty page so we have something to render
  if (pages.length === 0) {
    return [{ type: 'doc', content: [] }]
  }

  return pages
}

/**
 * Counts the total number of page breaks in a document
 *
 * @param {Object} tiptapJSON - TipTap document JSON
 * @returns {number} Number of page break nodes
 */
export function countPageBreaks(tiptapJSON) {
  if (!tiptapJSON || !tiptapJSON.content) {
    return 0
  }

  return tiptapJSON.content.filter((node) => node.type === 'pageBreak').length
}

/**
 * Checks if a page is empty (no content or only whitespace)
 *
 * @param {Object} pageJSON - TipTap page JSON
 * @returns {boolean} True if page is empty
 */
export function isPageEmpty(pageJSON) {
  if (!pageJSON || !pageJSON.content || pageJSON.content.length === 0) {
    return true
  }

  // Check if all nodes are empty
  for (const node of pageJSON.content) {
    // If node has text content, check if it's just whitespace
    if (node.type === 'paragraph' || node.type === 'heading') {
      if (node.content && node.content.length > 0) {
        // Has content - check if it's meaningful
        const hasText = node.content.some(
          (child) => child.type === 'text' && child.text && child.text.trim().length > 0
        )
        if (hasText) {
          return false // Found real content
        }
      }
    } else if (node.type !== 'paragraph' && node.type !== 'heading') {
      // Non-text nodes (images, code blocks, custom nodes, etc.) count as content
      return false
    }
  }

  return true // All nodes are empty
}

/**
 * Filters out empty pages from a pages array
 *
 * @param {Array<Object>} pages - Array of page JSON objects
 * @returns {Array<Object>} Pages with empty pages removed
 */
export function filterEmptyPages(pages) {
  const filtered = pages.filter((page) => !isPageEmpty(page))

  // Ensure we always return at least one page
  return filtered.length > 0 ? filtered : [{ type: 'doc', content: [] }]
}

/**
 * Gets page statistics for a document
 *
 * @param {Object} tiptapJSON - TipTap document JSON
 * @returns {Object} Statistics object
 *
 * @example
 * const stats = getPageStatistics(content)
 * // Returns: {
 * //   totalPages: 3,
 * //   pageBreaks: 2,
 * //   emptyPages: 0,
 * //   hasContent: true
 * // }
 */
export function getPageStatistics(tiptapJSON) {
  const pages = splitContentByPageBreaks(tiptapJSON)
  const emptyPages = pages.filter((page) => isPageEmpty(page)).length

  return {
    totalPages: pages.length,
    pageBreaks: countPageBreaks(tiptapJSON),
    emptyPages,
    hasContent: pages.length > 0 && !isPageEmpty(pages[0]),
  }
}
